'use client';
import React from 'react';
import AppLayout from '@/components/AppLayout';
import { Globe, TrendingUp, Building2 } from 'lucide-react';
import Link from 'next/link';

const regions = [
  {
    id: 'reg-001',
    name: 'Taiwan Strait',
    risk: 'High',
    events: 3,
    trend: 'Escalating',
    description: 'PLA naval exercises ongoing. Semiconductor supply chain at risk. 55% escalation probability within 10 days.',
    acmeExposure: '3 component suppliers in Taiwan — procurement risk elevated',
    escalationProb: 55,
    href: '/political-risk',
  },
  {
    id: 'reg-002',
    name: 'Red Sea / Yemen',
    risk: 'Critical',
    events: 5,
    trend: 'Escalating',
    description: 'Houthi drone activity disrupting major shipping lanes. Cape of Good Hope rerouting adds 12–14 days.',
    acmeExposure: '1 active shipment rerouted — +12 day delay confirmed',
    escalationProb: 78,
    href: '/maritime-risk',
  },
  {
    id: 'reg-003',
    name: 'Russia / Ukraine',
    risk: 'High',
    events: 8,
    trend: 'Stable',
    description: 'Ongoing conflict. Energy and commodity supply chains affected. EU sanctions expansion ongoing.',
    acmeExposure: 'Indirect energy commodity exposure — monitor sanctions list',
    escalationProb: 42,
    href: '/political-risk',
  },
  {
    id: 'reg-004',
    name: 'Iran / Gulf',
    risk: 'Critical',
    events: 4,
    trend: 'Escalating',
    description: 'New OFAC designations. Petrochemical sector exposure elevated. 2 Acme Corp suppliers flagged.',
    acmeExposure: '2 Tier-1 suppliers exposed to new OFAC SDN designations',
    escalationProb: 62,
    href: '/sanctions-tracker',
  },
  {
    id: 'reg-005',
    name: 'Sudan / NE Africa',
    risk: 'Medium',
    events: 2,
    trend: 'Stable',
    description: 'Civil conflict expansion. Humanitarian corridor closures. Low direct commercial exposure.',
    acmeExposure: 'Low direct exposure — logistics corridor monitoring active',
    escalationProb: 33,
    href: '/political-risk',
  },
  {
    id: 'reg-006',
    name: 'South China Sea',
    risk: 'Medium',
    events: 2,
    trend: 'Monitoring',
    description: 'Territorial disputes ongoing. Maritime traffic monitoring active. ASEAN shipping lanes under watch.',
    acmeExposure: 'APAC maritime routes — no immediate disruption',
    escalationProb: 28,
    href: '/maritime-risk',
  },
];

const riskStyle: Record<string, string> = {
  Critical: 'text-danger bg-danger/10 border-danger/20',
  High: 'text-warning bg-warning/10 border-warning/20',
  Medium: 'text-info bg-info/10 border-info/20',
  Low: 'text-success bg-success/10 border-success/20',
};

const trendStyle: Record<string, string> = {
  Escalating: 'text-danger',
  Stable: 'text-muted-foreground',
  Monitoring: 'text-info',
};

export default function RegionMonitorPage() {
  return (
    <AppLayout activeRoute="/region-monitor">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Region Monitor</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Real-time geopolitical risk monitoring by region — Acme Corp exposure mapped</p>
          </div>
          <Link href="/political-risk" className="flex items-center gap-2 text-sm bg-primary text-primary-foreground font-medium rounded-md px-4 py-2 hover:opacity-90 transition-all">
            Political Risk Analysis
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {regions.map(region => (
            <Link key={region.id} href={region.href} className="card-elevated rounded-xl border border-border p-4 hover:bg-muted/20 transition-colors cursor-pointer block">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Globe size={15} className="text-primary flex-shrink-0" />
                  <p className="text-sm font-semibold text-foreground">{region.name}</p>
                </div>
                <span className={`text-2xs font-semibold px-2 py-0.5 rounded border ${riskStyle[region.risk]}`}>{region.risk}</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-2">{region.description}</p>
              <div className="flex items-start gap-1.5 mb-3">
                <Building2 size={11} className="text-warning flex-shrink-0 mt-0.5" />
                <p className="text-2xs text-warning">{region.acmeExposure}</p>
              </div>
              <div className="flex items-center justify-between text-2xs">
                <span className="text-muted-foreground">{region.events} active events</span>
                <div className="flex items-center gap-2">
                  <span className={`flex items-center gap-1 font-medium ${trendStyle[region.trend]}`}>
                    <TrendingUp size={10} />
                    {region.trend}
                  </span>
                  <span className="text-muted-foreground">·</span>
                  <span className={`font-medium ${region.escalationProb > 50 ? 'text-danger' : region.escalationProb > 35 ? 'text-warning' : 'text-info'}`}>
                    {region.escalationProb}% esc.
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
